import type { ScheduleMembership } from "@Simplicity-Education/core";
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@simplicity/ui";
import { summarizeEntries } from "../config/schedules/scheduleRows";

function multiInstanceReason(membership: ScheduleMembership): string {
  return `The ${membership.schedule} schedule holds ${membership.instances} instances of this table. Sync now does not support multi-instance tables yet.`;
}

function optionLabel(membership: ScheduleMembership): string {
  if (membership.instances > 1) {
    return `${membership.schedule}: ${membership.instances} instances, not supported here`;
  }
  const summary = summarizeEntries(membership.entries);
  return summary ? `${membership.schedule}: ${summary}` : membership.schedule;
}

function SingleSchedule({ membership }: { membership: ScheduleMembership }) {
  if (membership.instances > 1) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="sync-schedule-note">
        {multiInstanceReason(membership)}
      </p>
    );
  }
  const summary = summarizeEntries(membership.entries);
  return (
    <p className="text-sm text-muted-foreground" data-testid="sync-schedule-note">
      Syncs with the {membership.schedule} schedule&apos;s settings{summary ? `: ${summary}` : "."}
    </p>
  );
}

/**
 * SIM-2273: which schedule's settings a manual sync runs with. A table on several schedules has
 * no default — each pulls a different scope — so the choice is required.
 */
export function SyncScheduleChoice({
  memberships,
  value,
  onChange,
}: {
  memberships: ScheduleMembership[];
  value: string | undefined;
  onChange: (schedule: string) => void;
}) {
  if (memberships.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="sync-schedule-note">
        Not on any schedule, so this sync uses the connector&apos;s default settings.
      </p>
    );
  }
  if (memberships.length === 1) return <SingleSchedule membership={memberships[0]} />;
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor="sync-schedule-choice">Sync as schedule</Label>
      <Select value={value ?? ""} onValueChange={onChange}>
        <SelectTrigger id="sync-schedule-choice" aria-label="Sync as schedule">
          <SelectValue placeholder="Choose a schedule" />
        </SelectTrigger>
        <SelectContent>
          {memberships.map((m) => (
            <SelectItem key={m.schedule} value={m.schedule} disabled={m.instances > 1}>
              {optionLabel(m)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        This table is on {memberships.length} schedules, each with its own scope. Choose one.
      </p>
    </div>
  );
}
